import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck } from 'lucide-react';
import { getCategoryBySlug } from '../../config/categories.config';
import { useUIStore } from '../../store';
import { useLastRun } from '../../hooks/useNews';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useReportEmailPreference,
  useSaveReportEmailPreference,
} from '../../hooks/useNotifications';
import { LanguageDropdown } from './LanguageDropdown';

type TFunc = ReturnType<typeof useTranslation>['t'];

/**
 * Breadcrumb for the current route: a section, then the page inside it.
 * The sections mirror the sidebar groups so the two never disagree.
 */
function getBreadcrumb(pathname: string, t: TFunc): { section: string; label: string } {
  const slug = pathname.replace(/^\//, '').split('/')[0];
  const g = (k: string) => t(`nav.groups.${k}`);

  const fixed: Record<string, { section: string; label: string }> = {
    '': { section: g('intelligence'), label: t('categories.dashboard') },
    dashboard: { section: g('intelligence'), label: t('categories.dashboard') },
    'daily-brief': { section: g('intelligence'), label: t('nav.dailyBrief') },
    'value-chain': { section: g('coverage'), label: t('nav.valueChain') },
    region: { section: g('regions'), label: t('nav.groups.regions') },
    rec: { section: g('regions'), label: t('nav.groups.regions') },
    'regional-intel': { section: g('analysis'), label: t('nav.regionalIntel') },
    'ground-truth': { section: g('analysis'), label: t('nav.groundTruth') },
    'system-health': { section: g('analysis'), label: t('nav.systemHealth') },
    'weekly-reports': { section: g('analysis'), label: t('nav.weeklyReports') },
    'africa-monthly': { section: g('analysis'), label: t('nav.africaMonthly') },
    'stratmined-weekly': { section: g('analysis'), label: t('nav.stratminedWeekly') },
    briefs: { section: g('analysis'), label: t('nav.strategicBriefs') },
    baraza: { section: g('analysis'), label: t('nav.barazaForum') },
    cameroon: { section: g('analysis'), label: t('nav.cameroonIntel') },
    alerts: { section: g('workspace'), label: t('nav.myAlerts') },
    bookmarks: { section: g('workspace'), label: t('nav.myBookmarks') },
    archive: { section: g('workspace'), label: t('nav.archive') },
    subscription: { section: g('workspace'), label: t('nav.subscription') },
    support: { section: g('workspace'), label: t('nav.support') },
    settings: { section: g('workspace'), label: t('nav.settings') },
  };

  if (fixed[slug]) return fixed[slug];

  const category = getCategoryBySlug(slug);
  if (category) {
    const section = slug === 'market-insights' ? g('intelligence') : g('coverage');
    return { section, label: t(`categories.${slug}`) };
  }

  return { section: t('header.sections.portal'), label: slug };
}

/**
 * Top bar — Brand Standards v2.
 *
 * 64px white band: breadcrumb on the left; on the right the aggregation sync
 * line, the language dropdown and the notification bell. Identity and search
 * both live in the sidebar now, so neither is repeated here.
 */
export const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { pageItemCount } = useUIStore();
  const { data: lastRunData } = useLastRun();

  // The bell used to count an in-memory store that nothing ever wrote to, so it
  // was permanently zero. It now reads the reader's own stored notifications.
  const { data: feed } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const { data: reportEmails } = useReportEmailPreference();
  const saveReportEmails = useSaveReportEmailPreference();
  const [panelOpen, setPanelOpen] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  const breadcrumb = getBreadcrumb(location.pathname, t);
  const unreadCount = feed?.unread ?? 0;
  const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-GB';

  // Close on an outside click or Escape, so the panel never traps the page.
  React.useEffect(() => {
    if (!panelOpen) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setPanelOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPanelOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen]);

  const openNotification = (id: number, link: string | null, unread: boolean) => {
    if (unread) markRead.mutate(id);
    setPanelOpen(false);
    if (link) navigate(link);
  };

  const relative = (iso: string): string => {
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (mins < 60) return rtf.format(-mins, 'minute');
    if (mins < 60 * 24) return rtf.format(-Math.round(mins / 60), 'hour');
    return rtf.format(-Math.round(mins / 1440), 'day');
  };

  // "Sync 09:42 GMT+1 · next 10:00" — both halves are real: the last completed
  // aggregation, and the next one the server's cron will fire.
  const syncLine = React.useMemo(() => {
    const hm = (iso: string) =>
      new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
    const tz = new Intl.DateTimeFormat(locale, { timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;

    const parts: string[] = [];
    const completedAt = lastRunData?.lastRun?.completedAt;
    if (completedAt) {
      parts.push(t('header.sync', { time: hm(completedAt), tz: tz ?? '' }).trim());
    }
    // aggregation_runs can be empty even while articles are landing, so the
    // next scheduled run still gets shown on its own rather than the line
    // disappearing entirely.
    if (lastRunData?.nextRunAt) {
      parts.push(t('header.nextSync', { time: hm(lastRunData.nextRunAt) }));
    }
    return parts.length ? parts.join(' · ') : null;
  }, [lastRunData, locale, t]);

  return (
    <header className="sm-print-hide sticky top-0 z-10 h-16 flex-none bg-white border-b border-[#EDE6DE] flex items-center gap-[14px] px-7">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[12.5px] font-medium text-[#6B7280]">{breadcrumb.section}</span>
        <span className="text-[#6B7280]/[.45]">/</span>
        <span className="text-[12.5px] font-semibold text-[#1B2A4A] truncate">{breadcrumb.label}</span>
        {pageItemCount != null && (
          <span
            className="ml-1 text-[11px] text-[#6B7280] whitespace-nowrap"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {t('header.itemCount', { count: pageItemCount })}
          </span>
        )}
      </div>

      <div className="flex-1" />

      {/* Aggregation sync line */}
      {syncLine && (
        <span
          className="hidden md:block text-[11px] text-[#6B7280] tracking-[.02em]"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {syncLine}
        </span>
      )}

      <LanguageDropdown />

      {/* Notification bell */}
      <div className="relative" ref={panelRef}>
        <button
          onClick={() => setPanelOpen((v) => !v)}
          className="relative w-[34px] h-[34px] rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-[#F8FAFC] hover:text-[#1B2A4A] transition-colors"
          aria-label={t('header.notifications')}
          aria-expanded={panelOpen}
          aria-haspopup="true"
        >
          <Bell size={18} strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              className="absolute top-0.5 right-0 min-w-[15px] h-[15px] px-1 rounded-full bg-[#C5922E] text-white text-[9px] font-bold flex items-center justify-center"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {panelOpen && (
          <div className="absolute right-0 top-[42px] w-[360px] max-h-[440px] flex flex-col bg-white rounded-xl border border-[#EDE6DE] shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#EDE6DE]">
              <span className="text-[12.5px] font-semibold text-[#1B2A4A]">
                {t('header.notifications')}
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="flex items-center gap-1 text-[11px] text-[#6B7280] hover:text-[#1B2A4A] disabled:opacity-50"
                >
                  <CheckCheck size={13} /> {t('notifications.markAllRead')}
                </button>
              )}
            </div>

            <div className="overflow-y-auto">
              {!feed?.items.length ? (
                <p className="px-4 py-8 text-center text-[12.5px] text-[#6B7280]">
                  {t('notifications.empty')}
                </p>
              ) : (
                feed.items.map((n) => {
                  const unread = !n.readAt;
                  return (
                    <button
                      key={n.id}
                      onClick={() => openNotification(n.id, n.link, unread)}
                      className={`w-full text-left px-4 py-3 border-b border-[#F1EBE4] last:border-b-0 hover:bg-[#F8FAFC] transition-colors ${
                        unread ? 'bg-[#FDFBF7]' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {unread && (
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5922E] flex-none" aria-hidden />
                        )}
                        <div className={unread ? '' : 'pl-[14px]'}>
                          <div className="text-[12.5px] font-semibold text-[#1B2A4A] leading-snug">{n.title}</div>
                          {n.body && (
                            <div className="text-[12px] text-[#6B7280] leading-snug mt-0.5">{n.body}</div>
                          )}
                          <div
                            className="text-[10.5px] text-[#9CA3AF] mt-1"
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          >
                            {relative(n.createdAt)}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* The opt-in lives here rather than in Settings, which is
                admin-only: a reader who cannot reach Settings could never ask
                for the email, and the subscription list would stay empty. */}
            <label className="flex items-start gap-2.5 px-4 py-3 border-t border-[#EDE6DE] bg-[#FDFCFA] cursor-pointer">
              <input
                type="checkbox"
                checked={!!reportEmails}
                onChange={(e) => saveReportEmails.mutate(e.target.checked)}
                disabled={saveReportEmails.isPending}
                className="mt-0.5 accent-[#C5922E]"
              />
              <span>
                <span className="block text-[12px] font-medium text-[#1B2A4A] leading-snug">
                  {t('notifications.emailPreference')}
                </span>
                <span className="block text-[11px] text-[#9CA3AF] leading-snug mt-0.5">
                  {t('notifications.emailPreferenceHelp')}
                </span>
              </span>
            </label>
          </div>
        )}
      </div>
    </header>
  );
};
